import { OrderLineStopType } from 'src/types/enums';
import classNames from '@utils/classnames';
import { CalendarDotsIcon, CheckIcon, ClockIcon, XIcon } from '@phosphor-icons/react';
import useSWR from 'swr';

import { formatDate, formatInputTime } from '../../../../../utils/date';
import { useCreatePurchaseCtx } from './context';
import { nullthrows } from '../../../../../utils/invariant';
import { fetchMinimalOrders, type IFetchOrdersQuery } from 'src/app/order/models';
import { useMemo } from 'react';

export interface Props {
  searchValue: string;
}

function ordersFetcher([_key, query]: [string, IFetchOrdersQuery]) {
  return fetchMinimalOrders(query);
}

export const OrderSelect: React.FC<Props> = (props) => {
  const { searchValue } = props;
  const { state, dispatch } = useCreatePurchaseCtx();
  const supplier = nullthrows(state.selectedSupplier, 'No supplier selected');
  const query: IFetchOrdersQuery = useMemo(() => {
    return {
      filters: {
        supplierId: supplier.id,
        purchaseId: null,
        orderNumber: searchValue || undefined,
      },
      orderBy: {
        orderNumber: 'asc',
      },
      take: 100,
    };
  }, [supplier.id, searchValue]);
  const { data, isLoading } = useSWR([`/api/orders/internal-minimal-list`, query], ordersFetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 60000,
  });

  const orders = data?.orders ?? [];
  const selectedOrderIds = new Set(state.orders.map((o) => o.id));

  return (
    <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
      {!orders.length && !isLoading && <div>Geen resultaten</div>}

      {!orders.length && isLoading && <div>Loading...</div>}

      {orders.map((o) => {
        const isSelected = selectedOrderIds.has(o.id);
        return (
          <div
            key={o.id}
            className={classNames('card p-4', {
              'selected-card': isSelected,
            })}
            data-clickable="true"
            onClick={() => {
              dispatch({
                type: 'SELECT_ORDER',
                order: o,
              });
            }}
          >
            <div className="card-heading-text">{o.orderNumber}</div>

            {o.lines.map((line) => {
              return (
                <div>
                  {line.stops
                    .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
                    .map((stop) => {
                      return (
                        <div className="grid grid-cols-2 my-1">
                          <div className="font-medium">{stop.type === OrderLineStopType.Load ? 'Laden' : 'Lossen'}</div>
                          <div>
                            {stop.location.country}-{stop.location.name}
                          </div>

                          <div className="flex gap-2 items-center">
                            <CalendarDotsIcon className="w-4 h-4" />
                            {formatDate(stop.date)}
                          </div>
                          <div className="flex gap-2 items-center">
                            <ClockIcon className="w-4 h-4" />
                            {`${formatInputTime(stop.timeStart)}-${formatInputTime(stop.timeEnd)}`}
                          </div>
                        </div>
                      );
                    })}
                </div>
              );
            })}

            <div className="flex font-medium items-center">
              CMR{' '}
              {o.hasCmr ? (
                <CheckIcon className="w-6 h-6 text-positive-text" />
              ) : (
                <XIcon className="w-6 h-6 text-negative-text" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
